import React, { useState, useEffect, useRef } from 'react';
import { Search } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface SearchCriteria {
  key: string;
  label: string;
}

const getSearchCriteria = (t: any): SearchCriteria[] => [
  { key: 'ticket_id', label: t('search.criteria.ticketId', 'Ticket ID') },
  { key: 'ticket_title', label: t('search.criteria.ticketTitle', 'Ticket Title') },
  { key: 'ticket_description', label: t('search.criteria.ticketDescription', 'Ticket Description') },
  { key: 'customer_id', label: t('search.criteria.customerId', 'Customer ID') },
  { key: 'customer_full_name', label: t('search.criteria.customerName', 'Customer Name') },
  { key: 'customer_phone', label: t('search.criteria.customerPhone', 'Customer Phone') },
  { key: 'customer_external_ref', label: t('search.criteria.customerReference', 'Customer Reference') },
];

interface SearchBarProps {
  onSearch?: (criteria: string, value: string) => void;
  onClearResults?: () => void;
  autoFocus?: boolean;
}

export default function SearchBar({ onSearch, onClearResults, autoFocus }: SearchBarProps) {
  const [searchText, setSearchText] = useState('');
  const [showCriteria, setShowCriteria] = useState(false);
  const [filteredCriteria, setFilteredCriteria] = useState<SearchCriteria[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const { t } = useTranslation();
  
  // Get search criteria with translations
  const searchCriteria = getSearchCriteria(t);

  useEffect(() => {
    // Handle clicks outside of the dropdown
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCriteria(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset selected index when filtered criteria changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredCriteria]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    
    // Check if we're typing after @ symbol
    const atIndex = value.lastIndexOf('@');
    if (atIndex !== -1) {
      const searchAfterAt = value.slice(atIndex + 1);
      const colonIndex = searchAfterAt.indexOf(':');
      
      // Clear results when @ is typed
      onClearResults?.();
      
      // If there's no colon yet, show and filter criteria
      if (colonIndex === -1) {
        setShowCriteria(true);
        const filtered = searchCriteria.filter(criteria =>
          criteria.label.toLowerCase().includes(searchAfterAt.toLowerCase()) ||
          criteria.key.toLowerCase().includes(searchAfterAt.toLowerCase())
        );
        setFilteredCriteria(filtered);
      } else {
        setShowCriteria(false);
      }
    } else {
      setShowCriteria(false);
    }
  };

  const handleSearch = () => {
    if (searchText.includes('@') && searchText.includes(':')) {
      const matches = searchText.match(/@(\w+):\s*(.+)/);
      if (matches) {
        const [, criteria, value] = matches;
        onSearch?.(criteria, value.trim());
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showCriteria && filteredCriteria.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % filteredCriteria.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev - 1 + filteredCriteria.length) % filteredCriteria.length);
          break;
        case 'Enter':
          e.preventDefault();
          if (showCriteria) {
            handleCriteriaClick(filteredCriteria[selectedIndex]);
          } else {
            handleSearch();
          }
          break;
        case 'Escape':
          setShowCriteria(false);
          break;
        case 'Tab':
          if (showCriteria) {
            e.preventDefault();
            handleCriteriaClick(filteredCriteria[selectedIndex]);
          }
          break;
      }
    } else if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleCriteriaClick = (criteria: SearchCriteria) => {
    const atIndex = searchText.lastIndexOf('@');
    if (atIndex !== -1) {
      const newText = searchText.slice(0, atIndex) + `@${criteria.key}: `;
      setSearchText(newText);
      setShowCriteria(false);
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.selectionStart = newText.length;
        inputRef.current.selectionEnd = newText.length;
      }
    }
  };

  const scrollSelectedIntoView = (index: number) => {
    const element = document.getElementById(`criteria-${index}`);
    if (element) {
      element.scrollIntoView({ block: 'nearest' });
    }
  };

  // Scroll selected item into view when it changes
  useEffect(() => {
    if (showCriteria) {
      scrollSelectedIntoView(selectedIndex);
    }
  }, [selectedIndex]);

  return (
    <div className="relative w-full">
      <div className="relative flex items-center">
        <input
          ref={inputRef}
          type="text"
          value={searchText}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className={`w-full pl-10 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:border-blue-500 ${
            theme === 'dark' 
              ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400' 
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
          }`}
          placeholder={t('search.placeholder', 'Search tickets... (e.g. @ticket_id: 123)')}
          autoFocus={autoFocus}
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className={`h-4 w-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
        </div>
        <button
          onClick={handleSearch}
          className="absolute inset-y-0 right-0 px-3 flex items-center bg-blue-500 text-white rounded-r-lg hover:bg-blue-600 focus:outline-none"
          aria-label="Search"
        >
          <Search className="h-4 w-4" />
        </button>
      </div>
      {showCriteria && filteredCriteria.length > 0 && (
        <div
          ref={dropdownRef}
          className={`absolute z-10 w-full mt-1 border rounded-md shadow-lg max-h-60 overflow-auto ${
            theme === 'dark'
              ? 'bg-gray-800 border-gray-700'
              : 'bg-white border-gray-200'
          }`}
        >
          {filteredCriteria.map((criteria, index) => (
            <button
              id={`criteria-${index}`}
              key={criteria.key}
              onClick={() => handleCriteriaClick(criteria)}
              onMouseEnter={() => setSelectedIndex(index)}
              className={`w-full px-4 py-2 text-left ${
                theme === 'dark'
                  ? `${index === selectedIndex 
                      ? 'bg-gray-700 text-blue-400' 
                      : 'hover:bg-gray-700 text-gray-200'}`
                  : `${index === selectedIndex 
                      ? 'bg-blue-50 text-blue-700' 
                      : 'hover:bg-gray-100 text-gray-900'}`
              } focus:outline-none`}
            >
              <span className="font-medium">{criteria.label}</span>
              <span className={`text-sm ml-2 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>@{criteria.key}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}